import {
    AppState,
    BreachEvent,
    buildRedirectUrl,
    click_origin_breach_screen,
    ConfigurationContext,
    getLoginUrl,
    handleSwGPurchase,
    useFeature,
} from '@news-mono/web-common'
import React, { useContext } from 'react'
import { useSelector } from 'react-redux'
import { IconStar } from '../../../../icons/IconStar/IconStar'
import { useAuthActions } from '../../../../user-registration/authentication/AuthenticationProvider'
import { useSection } from '../../../../__helpers'
import { TheWestSection } from '../../../../__styling'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import {
    Ribbon,
    StyledPackageImage,
    StyledSubscribeWithGoogleButton,
    StyledSubscriptionCancellationMessage,
    StyledSubscriptionPackage,
    StyledSubscriptionPackageActions,
    StyledSubscriptionPackageButton,
    StyledSubscriptionPackageHeading,
    StyledSubscriptionPackageOriginalPrice,
    StyledSubscriptionPackagePrice,
    StyledSubscriptionPackagePriceQualifier,
} from './SubscriptionPackage.styled'

const everyDayDigitalImage = require('./images/everyday_digital.png')
const everyDayNewspaperImage = require('./images/everyday_digital_newspaper.png')

export interface SubscriptionPackageProps {
    pretitle: string
    originalPrice?: string
    heading: string
    price: string
    priceQualifier?: string
    ribbonTitle?: string
    packagePath: string
    offerCode?: string
    packageType?: 'digital' | 'digitalprint'
    breachCreativeName: string
    breachDeal: string
    packageTheme?: 'default' | 'red'
    cancellationMessage?: string
    googleSKU?: string
    onEvent: (event: BreachEvent) => void
}

export const SubscriptionPackage: React.FC<SubscriptionPackageProps> = ({
    pretitle,
    heading,
    originalPrice,
    price,
    priceQualifier,
    ribbonTitle,
    packagePath,
    breachDeal,
    offerCode,
    breachCreativeName,
    packageTheme = 'default',
    onEvent,
    packageType,
    cancellationMessage,
    googleSKU,
}) => {
    const { onSubscribeClick } = useAuthActions()
    const subscribeWithGoogleEnabled = useFeature('subscribe-with-google')
    const section = useSection<TheWestSection>()

    // Matches swg-breach-albany-advertiser etc
    const subscribeWithGoogleBreachInterfaceEnabled = useFeature(
        `swg-breach-${section}`,
    )

    const { subscriptions } = useSelector((state: AppState) => state.SwG)
    const { isLoggedIn, userEmail } = useSelector(
        (state: AppState) => state.authentication,
    )

    const showSwG =
        subscribeWithGoogleEnabled &&
        subscribeWithGoogleBreachInterfaceEnabled &&
        googleSKU

    const localConfig = useContext(ConfigurationContext)

    const handleSubscribeWithGoogleButton = (sku?: string) => {
        if (subscriptions && sku) {
            // Setup callback for payment response, noting that this callback only gets triggered when the user successfully subscribes to the SKU
            subscriptions.setOnPaymentResponse((paymentResponse) => {
                paymentResponse.then(async (response) => {
                    // At this point, we need to handle the userData as well as purchaseData from the user
                    // Cast responseObject to object.
                    const responseObject = await handleSwGPurchase(
                        localConfig,
                        response,
                    )

                    // Update the frontend prompt to tell that the user that their account has been created.

                    response.complete().then(() => {
                        // Once the user has acknowledged that their account has been created, we need to then redirect to the authSite to finish their login
                        if (responseObject) {
                            // Need to see if the user is logged in to the account they bought the SKU with
                            const isSameAccount =
                                response.userData.email === userEmail

                            window.location.href =
                                isLoggedIn && isSameAccount
                                    ? buildRedirectUrl(window.location)
                                    : getLoginUrl({
                                          prompt: 'google',
                                          login_hint: responseObject.sub,
                                      })
                        }
                    })
                })
            })

            subscriptions?.subscribe(sku)
        }
    }

    return (
        // Note: There is high editorial care factor on these images, please ensure
        // appropriate approvals have happened before replacing or updating
        <StyledSubscriptionPackage packageTheme={packageTheme}>
            {packageType === 'digital' && (
                <StyledPackageImage
                    src={everyDayDigitalImage}
                    alt=""
                    role="presentation"
                />
            )}

            {packageType === 'digitalprint' && (
                <StyledPackageImage
                    src={everyDayNewspaperImage}
                    alt=""
                    role="presentation"
                />
            )}
            <StyledSubscriptionPackageHeading packageTheme={packageTheme}>
                {heading}
            </StyledSubscriptionPackageHeading>
            {originalPrice && (
                <StyledSubscriptionPackageOriginalPrice
                    packageTheme={packageTheme}
                >
                    {originalPrice}
                </StyledSubscriptionPackageOriginalPrice>
            )}
            <StyledSubscriptionPackagePrice packageTheme={packageTheme}>
                {price}
            </StyledSubscriptionPackagePrice>
            {priceQualifier && (
                <StyledSubscriptionPackagePriceQualifier
                    packageTheme={packageTheme}
                >
                    {priceQualifier}
                </StyledSubscriptionPackagePriceQualifier>
            )}
            {ribbonTitle && (
                <Ribbon packageTheme={packageTheme}>
                    <span>
                        <IconStar />
                        {ribbonTitle}
                    </span>
                </Ribbon>
            )}
            <StyledSubscriptionPackageActions>
                <StyledSubscriptionPackageButton
                    packageTheme={packageTheme}
                    buttonWidth="wide"
                    onClick={() => {
                        createLinkClickEvent(
                            breachCreativeName,
                            breachDeal,
                            onEvent,
                        )()
                        onSubscribeClick({
                            packagePath,
                            offerCode,
                            callToAction: click_origin_breach_screen,
                        })
                    }}
                >
                    Subscribe Now
                </StyledSubscriptionPackageButton>

                {packageType === 'digital' && showSwG && (
                    <StyledSubscribeWithGoogleButton
                        onClick={() =>
                            handleSubscribeWithGoogleButton(googleSKU)
                        }
                        className="swg-button-light"
                    ></StyledSubscribeWithGoogleButton>
                )}
                <StyledSubscriptionCancellationMessage
                    packageTheme={packageTheme}
                >
                    {cancellationMessage}
                </StyledSubscriptionCancellationMessage>
            </StyledSubscriptionPackageActions>
        </StyledSubscriptionPackage>
    )
}

SubscriptionPackage.displayName = 'SubscriptionPackage'

export default SubscriptionPackage
